import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { Globe } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { LANGUAGES } from '~/components/features/communications/constants';
import { EmailEditor } from '~/components/features/communications/email-editor';
import { EmailPreview } from '~/components/features/communications/email-preview';
import { SmsEditor } from '~/components/features/communications/sms-editor';
import { SmsPreview } from '~/components/features/communications/sms-preview';
import { PageContainer } from '~/components/layouts/page-container';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { communicationsQueryOptions } from '~/lib/communications';
import { resolveSlugIds } from '~/lib/slug-resolver';
import { cn } from '~/lib/utils';

const MESSAGE_TYPES = ['invite', 'reminder'] as const;
type MessageType = (typeof MESSAGE_TYPES)[number];

const typeSearchSchema = z.object({
  lang: z.string().length(2).default('en')
});

export const Route = createFileRoute(
  '/_authenticated/org/$orgSlug/department/$deptSlug/communications/$type'
)({
  validateSearch: typeSearchSchema,
  beforeLoad: ({ params }) => {
    if (!(MESSAGE_TYPES as readonly string[]).includes(params.type)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/org/$orgSlug/department/$deptSlug/communications',
        params: { orgSlug: params.orgSlug, deptSlug: params.deptSlug }
      });
    }
  },
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ context, params, deps }) => {
    const { orgId, deptId } = resolveSlugIds(context.queryClient, params.orgSlug, params.deptSlug);
    if (orgId && deptId) {
      void context.queryClient.prefetchQuery(communicationsQueryOptions(orgId, deptId, deps.lang));
    }
  },
  component: TemplateEditPage
});

type ActiveTab = 'sms' | 'email';

interface TemplateEditorProps {
  orgId: string;
  deptId: string;
  orgSlug: string;
  deptSlug: string;
  messageType: MessageType;
  lang: string;
}

function TemplateEditor({
  orgId,
  deptId,
  orgSlug,
  deptSlug,
  messageType,
  lang
}: TemplateEditorProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(communicationsQueryOptions(orgId, deptId, lang));
  const emailData = data?.[messageType].email;
  const smsData = data?.[messageType].sms;

  const [activeTab, setActiveTab] = useState<ActiveTab>('sms');
  const [emailSubject, setEmailSubject] = useState<string>(emailData?.subject ?? '');
  const [emailBody, setEmailBody] = useState<string>(emailData?.body ?? '');
  const [smsBody, setSmsBody] = useState<string>(smsData?.body ?? '');

  // Sync when data arrives after a mutation (reset/save) or first load
  useEffect(() => {
    if (emailData) setEmailSubject(emailData.subject);
  }, [emailData?.subject]);

  useEffect(() => {
    if (emailData) setEmailBody(emailData.body);
  }, [emailData?.body]);

  useEffect(() => {
    if (smsData) setSmsBody(smsData.body);
  }, [smsData?.body]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ['communications', 'templates', orgId, deptId]
    });
  };

  const saveEmailMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].department[':deptId'].communications[
        'email-templates'
      ].$put({
        param: { orgId, deptId },
        json: {
          departmentId: deptId,
          type: messageType,
          language: lang,
          subject: emailSubject,
          body: emailBody
        }
      });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.saved'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const saveSmsMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].department[':deptId'].communications[
        'sms-templates'
      ].$put({
        param: { orgId, deptId },
        json: {
          departmentId: deptId,
          type: messageType,
          language: lang,
          body: smsBody
        }
      });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.saved'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const resetEmailMutation = useMutation({
    mutationFn: async () => {
      const id = emailData?.id;
      if (!id) return;
      const res = await api.api.org[':orgId'].department[':deptId'].communications[
        'email-templates'
      ][':templateId'].$delete({
        param: { orgId, deptId, templateId: id }
      });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.resetDone'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const resetSmsMutation = useMutation({
    mutationFn: async () => {
      const id = smsData?.id;
      if (!id) return;
      const res = await api.api.org[':orgId'].department[':deptId'].communications['sms-templates'][
        ':templateId'
      ].$delete({
        param: { orgId, deptId, templateId: id }
      });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      invalidate();
      toast.success(t('communications.actions.resetDone'));
    },
    onError: () => toast.error(t('errors.unexpected'))
  });

  const titleKey =
    messageType === 'invite' ? 'communications.invite.title' : 'communications.reminder.title';
  const descKey =
    messageType === 'invite'
      ? 'communications.invite.description'
      : 'communications.reminder.description';

  const isSaving = activeTab === 'sms' ? saveSmsMutation.isPending : saveEmailMutation.isPending;
  const isResetting =
    activeTab === 'sms' ? resetSmsMutation.isPending : resetEmailMutation.isPending;
  const isCustomized =
    activeTab === 'sms' ? (smsData?.isCustomized ?? false) : (emailData?.isCustomized ?? false);

  return (
    <>
      <div className="flex flex-col gap-1 mb-2">
        <Link
          to="/org/$orgSlug/department/$deptSlug/communications"
          params={{ orgSlug, deptSlug }}
          search={{ lang }}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          ← {t('communications.title')}
        </Link>
        <h2 className="text-lg font-semibold">{t(titleKey)}</h2>
        <p className="text-sm text-muted-foreground">{t(descKey)}</p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-6 border-b border-border">
        {(['sms', 'email'] as ActiveTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => {
              setActiveTab(tab);
            }}
            className={cn(
              '-mb-px border-b-2 pb-3 text-sm font-medium transition-colors',
              activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {tab === 'sms' ? t('communications.tabs.sms') : t('communications.tabs.email')}
          </button>
        ))}
      </div>

      {/* Two-column layout: editor + preview */}
      <div className="grid grid-cols-1 gap-8 pt-2 lg:grid-cols-2">
        {/* Editor */}
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div className="h-48 animate-pulse rounded-lg bg-muted" />
          ) : activeTab === 'sms' ? (
            <SmsEditor value={smsBody} onChange={setSmsBody} disabled={isSaving || isResetting} />
          ) : (
            <EmailEditor
              subject={emailSubject}
              body={emailBody}
              onSubjectChange={setEmailSubject}
              onBodyChange={setEmailBody}
              disabled={isSaving || isResetting}
            />
          )}

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              onClick={() => {
                if (activeTab === 'sms') resetSmsMutation.mutate();
                else resetEmailMutation.mutate();
              }}
              disabled={!isCustomized || isResetting || isSaving}
            >
              {t('communications.actions.reset')}
            </Button>
            <Button
              onClick={() => {
                if (activeTab === 'sms') saveSmsMutation.mutate();
                else saveEmailMutation.mutate();
              }}
              disabled={isSaving}
            >
              {t('communications.actions.save')}
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="hidden lg:flex lg:flex-col lg:items-center lg:pt-2">
          {activeTab === 'sms' ? (
            <SmsPreview body={smsBody} />
          ) : (
            <EmailPreview subject={emailSubject} body={emailBody} />
          )}
        </div>
      </div>
    </>
  );
}

function TemplateEditPage(): React.ReactNode {
  const { orgSlug, deptSlug, type } = Route.useParams();
  const { lang } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);

  const messageType = type as MessageType;

  return (
    <PageContainer
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger render={<Button variant="outline" size="sm" />}>
            <Globe className="size-4" />
            {LANGUAGES.find((l) => l.code === lang)?.label ?? lang.toUpperCase()}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup
              value={lang}
              onValueChange={(value: string) =>
                void navigate({ search: (_prev) => ({ lang: value }) })
              }
            >
              {LANGUAGES.map(({ code, label }) => (
                <DropdownMenuRadioItem key={code} value={code}>
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      {/* Remount editor when lang or messageType changes to reset local state */}
      {!orgId || !deptId ? (
        <div className="h-48 animate-pulse rounded-lg bg-muted" />
      ) : (
        <TemplateEditor
          key={`${lang}-${messageType}`}
          orgId={orgId}
          deptId={deptId}
          orgSlug={orgSlug}
          deptSlug={deptSlug}
          messageType={messageType}
          lang={lang}
        />
      )}
    </PageContainer>
  );
}
